import { useMemo } from "react"; // 1. Import useMemo
import { ApiSystem } from "../state/types";
import { fetchAuthSession } from "aws-amplify/auth";
import apiClient from "./apiClient";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const buildSystemPatch = (
  before: unknown,
  after: unknown,
  path = "",
  setPaths: Record<string, unknown> = {},
  removePaths: string[] = []
): { setPaths: Record<string, unknown>; removePaths: string[] } => {
  if (before === undefined) {
    if (path) {
      setPaths[path] = after;
    } else if (isPlainObject(after)) {
      Object.entries(after).forEach(([key, value]) => {
        setPaths[key] = value;
      });
    }
    return { setPaths, removePaths };
  }

  if (after === undefined) {
    if (path) removePaths.push(path);
    return { setPaths, removePaths };
  }

  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    keys.forEach((key) => {
      const nextPath = path ? `${path}.${key}` : key;
      buildSystemPatch(before[key], after[key], nextPath, setPaths, removePaths);
    });
    return { setPaths, removePaths };
  }

  if (Array.isArray(before) && Array.isArray(after)) {
    if (JSON.stringify(before) !== JSON.stringify(after) && path) {
      setPaths[path] = after;
    }
    return { setPaths, removePaths };
  }

  if (!Object.is(before, after) && path) {
    setPaths[path] = after;
  }

  return { setPaths, removePaths };
};

// This remains outside and stable
export async function getAuthHeaders() {
  const session = await fetchAuthSession({ forceRefresh: true });

  const idJwt = session.tokens?.idToken?.toString();
  if (!idJwt) throw new Error("No idToken available");

  return {
    Authorization: idJwt,
    "Content-Type": "application/json",
  };
}

export const useSystemsApi = () => {
  // 2. Wrap the return in useMemo
  return useMemo(() => {
    return {
      getSystemsApi: async () => {
        const { data } = await apiClient.get(`/system`);
        return data.systems as ApiSystem[];
      },

      createSystemApi: async (system: ApiSystem) => {
        await apiClient.post<ApiSystem>(`/system/create`, system);
        return system;
      },

      updateSystemApi: async (system: ApiSystem, previousSystem?: ApiSystem | null) => {
        const baseSystem = previousSystem || null;
        const { setPaths, removePaths } = buildSystemPatch(baseSystem, system);
        const payload =
          baseSystem == null
            ? { system }
            : {
                system: {
                  systemCode: system.systemCode,
                  ...setPaths,
                },
                removePaths,
              };

        await apiClient.put<ApiSystem>(`/system`, payload);
        return system;
      },

      deleteSystemApi: async (systemCode: string) => {
        await apiClient.delete(`/system/${systemCode}`);
      },
    };
  }, []); // 3. Empty dependencies ensure these function references stay the same
};
